import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dialog, Grid, TextField, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import { useSnackbar } from '../../../components/context/SnackbarContext';
import useAxios from '../../../hooks/useAxios';
import { useLoader } from "../../../components/context/LoaderContext";
import DynamicFields from "components/common/CustomFields";

function CreateNewAccountDialog({ onClose, open, handleReloadFunc, accountId }) {
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();
    const [customFields, setCustomFields] = useState([]);

    useEffect(() => {
        async function fetchData() {
            getData();
        }
        fetchData();
    }, []);


    const getData = async () => {
        showLoader();
        await axiosInstance.get('api/account/create').then((response) => {
            if (response.status === 200) {
                setCustomFields(response?.data?.data?.customFields || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                    }
                }
            });
    }

    const buildInitialValues = (customFields) => {
        const initial = {
            organization_name: '',
            organization_email: '',
            organization_phone1: '',
            organization_website: '',
        };

        customFields.forEach(field => {
            const key = field.field_name;

            switch (field.display_type) {
                case 'checkbox':
                    initial[key] = [];
                    break;
                case 'file':
                    initial[key] = [];
                    break;
                case 'date':
                case 'datetime':
                    initial[key] = '';
                    break;
                default:
                    initial[key] = '';
                    break;
            }
        });

        return initial;
    };

    const buildValidationSchema = (customFields) => {
        const schema = {
            organization_name: yup.string().trim('Company name cannot include space').strict(true).min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters').required('Enter company name'),
            organization_email: yup.string().email('Please enter valid email address').min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            organization_phone1: yup.string().max(25, 'Phone must be at most 25 characters'),
            organization_website: yup.string().trim('Website cannot include space').strict(true).min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters').required('Enter website name'),
        };

        customFields.forEach(field => {
            const key = field?.field_name;
            const label = field?.label || field?.field_name;
            const isRequired = field?.is_required === 'yes';

            switch (field.display_type) {
                case 'text':
                case 'textarea':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'email':
                    schema[key] = yup.string().email('Enter a valid email');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'phone':
                    schema[key] = yup
                        .string()
                        .matches(/^[0-9()+-\s]*$/, 'Enter a valid phone number');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'number':
                    schema[key] = yup
                        .number()
                        .typeError(`${label} must be a number`);
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'select':
                    if (field?.is_multiple === 'yes') {
                        // MULTI-SELECT
                        schema[key] = yup.array().of(yup.string());
                        if (isRequired) {
                            schema[key] = schema[key].min(1, `Select at least one ${label}`);
                        }
                    } else {
                        // SINGLE SELECT
                        schema[key] = yup.string();
                        if (isRequired) {
                            schema[key] = schema[key].required(`${label} is required`);
                        }
                    }
                    break;
                case 'radio':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'checkbox':
                    schema[key] = yup.array();
                    if (isRequired) schema[key] = schema[key].min(1, `Select at least one ${label}`);
                    break;

                case 'date':
                case 'datetime':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'file':
                    schema[key] = yup.array().of(
                        yup
                            .mixed()
                            .test('fileSize', 'File too large', value => !value || value.size <= 4 * 1024 * 1024)
                    );
                    if (isRequired) {
                        schema[key] = schema[key].min(1, `Upload at least one ${label}`);
                    }
                    break;

                default:
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;
            }
        });

        return yup.object().shape(schema);
    };

    const staticFields = ['organization_name', 'organization_email', 'organization_phone1', 'organization_website'];

    const handleSubmit = async (values, onSubmitProps) => {
        const formData = new FormData();
        const customFieldsData = {};

        Object.entries(values).forEach(([key, value]) => {
            if (staticFields.includes(key)) {
                // Static fields (e.g., first_name, etc.)
                if (Array.isArray(value)) {
                    value.forEach(file => formData.append(`${key}[]`, file));
                } else {
                    formData.append(key, value);
                }
            } else {
                // Custom fields
                const isFileArray = Array.isArray(value) && value.length && value[0] instanceof File;

                if (isFileArray) {
                    value.forEach((file, idx) => {
                        formData.append(`${key}[${idx}]`, file); // append each file individually
                    });
                } else {
                    customFieldsData[key] = value;
                }
            }
        });

        // Append custom fields JSON string under one key
        formData.append('custom_fields', JSON.stringify(customFieldsData));

        showLoader()
        await axiosInstance.post("/api/account/store", formData).then(response => {
            if (response.status === 200) {
                hideLoader()
                showSnackbar(response.data.message, 'success')
                onClose()
                handleReloadFunc()
            }
        }).then(json => {

        }).catch(error => {
            if (error.response) {
                hideLoader()
                onSubmitProps.setSubmitting(false)
                if (error.response.status === 422 || error.response.data.validation_errors) {
                    const errors = error.response.data.message;
                    Object.keys(errors).forEach(key => {
                        onSubmitProps.setFieldError(key, errors[key]);
                    });
                } else {
                    showSnackbar(error.response.data.message, 'error')
                }
            }
        });
    }
    return (
        <>
            <Dialog onClose={onClose} open={open}>
                <div className="dialog-title">
                    <h2>Create New Account</h2>
                    <Tooltip title="Close">
                        <button className="icon-button dialog-close" onClick={onClose}>
                            <Icon path={mdiClose} color={'currentColor'} size={1} />
                        </button>
                    </Tooltip>
                </div>
                <Formik
                    initialValues={buildInitialValues(customFields)}
                    validationSchema={buildValidationSchema(customFields)}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {props => {
                        const {
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue
                        } = props;
                        return (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    label="Company Name"
                                    variant="filled"
                                    name="organization_name"
                                    value={values.organization_name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.organization_name && touched.organization_name}
                                    helperText={(errors.organization_name && touched.organization_name) && errors.organization_name}
                                />

                                <TextField
                                    label="Company Email"
                                    variant="filled"
                                    name="organization_email"
                                    value={values.organization_email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.organization_email && touched.organization_email}
                                    helperText={(errors.organization_email && touched.organization_email) && errors.organization_email}
                                />

                                <TextField
                                    label="Company Phone"
                                    variant="filled"
                                    name="organization_phone1"
                                    value={values.organization_phone1}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.organization_phone1 && touched.organization_phone1}
                                    helperText={(errors.organization_phone1 && touched.organization_phone1) && errors.organization_phone1}
                                />
                                <TextField
                                    label="Website"
                                    variant="filled"
                                    name="organization_website"
                                    value={values.organization_website}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.organization_website && touched.organization_website}
                                    helperText={(errors.organization_website && touched.organization_website) && errors.organization_website}
                                />
                                {customFields?.length > 0 &&
                                    <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-12">Custom Fields</Typography>
                                }
                                {customFields?.length > 0 && customFields.map((field, idx) => (
                                    <DynamicFields
                                        key={idx}
                                        field={field}
                                        values={values}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                ))}

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button type="submit" className="btn-block">Create</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button color="secondary" className="btn-block" onClick={onClose}>Cancel</Button>
                                    </Grid>
                                </Grid>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog >
        </>
    );
}

export default CreateNewAccountDialog;